import useFetchData from 'app/hooks/useFetchData';
import { useEffect, useMemo } from 'react';
import { useReportPeriod } from '../components/ReportPeriodFilter';
import {
  ALL_MONTHS,
  IRankingItem,
  IReportPeriod,
  IReportRevenueItem,
} from '../constants';

export const DASHBOARD_REPORT_TYPE = {
  PARK: 'parkId',
  CATALOG: 'catalog',
  SHOW: 'show',
  AGENT: 'agent',
};

export interface IDashboardRevenueRow {
  reportYear: number;
  parkId?: number;
  parkName?: string;
  catalog?: string;
  name?: string;
  ticketQuantity?: number;
  totalAmount?: number;
}

export interface IReportItemName {
  name: string;
  fullName: string;
}

const buildPayload = (period: IReportPeriod, type: string) => ({
  year: period.year,
  type,
  ...(period.month === ALL_MONTHS ? {} : { month: period.month }),
});

const toRows = (result: any): IDashboardRevenueRow[] =>
  Array.isArray(result) ? result : result?.data ?? [];

export interface IUseDashboardReportParams {
  actionApi: any;
  type: string;
}

export const useDashboardReport = (params: IUseDashboardReportParams) => {
  const { actionApi, type } = params;
  const { result, onFetchData, isLoading } = useFetchData<any>({
    actionApi,
    IIFE: false,
  });
  const { period, onChangePeriod } = useReportPeriod({
    onChange: nextPeriod =>
      onFetchData({ payloadData: buildPayload(nextPeriod, type) }),
  });

  useEffect(() => {
    onFetchData({ payloadData: buildPayload(period, type) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => toRows(result), [result]);

  return { period, onChangePeriod, isLoading, rows };
};

export const groupRevenueRows = (
  rows: IDashboardRevenueRow[],
  getKey: (row: IDashboardRevenueRow) => string,
  getName: (key: string) => IReportItemName,
): IReportRevenueItem[] => {
  const revenueByKey = rows.reduce(
    (acc: Record<string, IReportRevenueItem>, row) => {
      const key = getKey(row);
      const current = acc[key] ?? {
        ...getName(key),
        revenue: 0,
        ticketCount: 0,
      };
      acc[key] = {
        ...current,
        revenue: current.revenue + (row?.totalAmount ?? 0),
        ticketCount: current.ticketCount + (row?.ticketQuantity ?? 0),
      };
      return acc;
    },
    {},
  );
  return Object.values(revenueByKey);
};

export const toRankingRows = (rows: IDashboardRevenueRow[]): IRankingItem[] =>
  rows.map((row, index) => ({
    id: `${index}-${row?.name ?? ''}`,
    name: row?.name ?? '',
    parkName: row?.parkName,
    revenue: row?.totalAmount ?? 0,
    ticketCount: row?.ticketQuantity ?? 0,
  }));

export default useDashboardReport;
